import {
  getSplitBucketsRemaining,
  getSplitPaymentsAmountAlreadyAdded,
  isFeelessPaymentMethod,
  projectSplitPaymentEffectiveCents,
} from '../../helpers/payments';
import { getSplitPaymentSchema } from '../../types/payments';
import useCashDiscount from './useCashDiscount';
import useSplitPayments from './useSplitPayments';

/**
 *
 * Derived state from the split payments group
 *
 */
function useSplitPaymentsState() {
  const {
    splitPaymentsGroup,
    paymentMethods,
    currentIndex,
    pendingTerminalPayment,
    form,
  } = useSplitPayments();
  const { cashDiscountEnabled } = useCashDiscount();

  const amountAlreadyAdded = getSplitPaymentsAmountAlreadyAdded(
    paymentMethods,
    currentIndex,
  );

  const currentPayment = paymentMethods[currentIndex];
  const isCurrentPaymentCreated = !!currentPayment?.paymentId;
  const isCurrentPaymentComplete =
    !!currentPayment && currentPayment?.isPaymentCollected;

  const isTerminalPending = !!pendingTerminalPayment;
  const paymentNumber = currentIndex + 1;

  const totalPriceCents = splitPaymentsGroup?.totalAmountCents || 0;
  const amountAlreadyAddedCents = Math.round(amountAlreadyAdded * 100);
  const allInRemainingCents = Math.max(
    0,
    totalPriceCents - amountAlreadyAddedCents,
  );

  const isCurrentFeelessUnderCd =
    cashDiscountEnabled && isFeelessPaymentMethod(form.paymentMethod);
  const { deflatedRem } = getSplitBucketsRemaining(
    splitPaymentsGroup,
    paymentMethods,
    currentIndex,
  );
  // "of $X" header stays all-in so it matches the helper's deducted amount.
  const remainingAmount = allInRemainingCents / 100;
  // Max face for the current payment: feeless under CD is capped to the
  // deflated remaining (face waives fee on top); everything else = all-in.
  const maxFaceCents = isCurrentFeelessUnderCd
    ? Math.min(allInRemainingCents, deflatedRem)
    : allInRemainingCents;
  const effectiveTotalPriceCents = amountAlreadyAddedCents + maxFaceCents;

  const amountNumber = form.amount ? Number(form.amount) : 0;
  const formToValidate = { ...form, amount: amountNumber };

  const { effectiveAmountCents } = projectSplitPaymentEffectiveCents({
    amountCents: Math.round(amountNumber * 100),
    method: form.paymentMethod,
    group: splitPaymentsGroup,
    paymentMethods,
    currentIndex,
    cashDiscountEnabled,
  });

  const validationSchema = getSplitPaymentSchema(
    effectiveTotalPriceCents,
    paymentMethods,
    currentIndex,
  );
  const isValid = validationSchema.isValidSync(formToValidate);

  const isComplete =
    totalPriceCents > 0 &&
    Math.round(amountAlreadyAdded * 100) + effectiveAmountCents >=
      totalPriceCents;

  return {
    amountAlreadyAdded,
    isCurrentPaymentCreated,
    isCurrentPaymentComplete,
    isTerminalPending,
    paymentNumber,
    remainingAmount,
    maxFaceAmount: maxFaceCents / 100,
    isValid,
    isComplete,
    totalPriceCents,
    effectiveTotalPriceCents,
    effectiveAmountCents,
    isCurrentFeelessUnderCd,
    validationSchema,
    parsedForm: formToValidate,
  };
}

export default useSplitPaymentsState;
